import { useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../../../Helpers/formating";
import { LINE_KINDS } from "./helpers";
import {
  fieldLabelStyle,
  selectStyle,
  getSelectMenuProps,
  textFieldStyle,
  headerRowStyle,
  headerColStyle,
  variantRowStyle,
  variantLeftGroupStyle,
  variantTrashStyle,
  variantPriceBoxStyle,
  unitChipStyle,
  variantInlinePriceStyle,
  amountAdornmentStyle,
  addLinkStyle,
  fieldErrorStyle,
  floatErrorStyle,
} from "./styles";

// One "Service" line card in the Edit Order editor (COMY1-292). Header row =
// "Choose Service or Product" + "Choose A Service" side by side; below it a
// compact row per variant (variant · amount · unit · price · remove).
const ServiceLineCard = ({
  index,
  line,
  services,
  servicesLoading,
  variantOptions,
  unitOptions,
  loadVariants,
  currencySymbol,
  changeKind,
  setLineField,
  setVariantField,
  addVariant,
  removeVariant,
  errors,
  showErrors,
  submitCount,
}) => {
  const { t, i18n } = useTranslation("view-job-modal");
  const menuProps = getSelectMenuProps(i18n.language === "he");

  // Ensure the chosen service's variants are loaded (e.g. seeded lines).
  useEffect(() => {
    if (line.service_id) loadVariants(line.service_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.service_id]);

  const options = variantOptions || [];

  const handleServiceChange = (value) => {
    setLineField(index, "service_id", value);
    const svc = services.find((s) => String(s.id) === String(value));
    setLineField(index, "service_label", svc?.label || "");
    loadVariants(value);
  };

  const handleVariantChange = (vIndex, value) => {
    const opt = options.find((o) => String(o.variant_id) === String(value));
    // Picking a variant resets the row's error state — new requirements it
    // introduces (e.g. a price for a not-listed variant) only flag on the
    // next save attempt, not the moment it's chosen.
    setVariantField(index, vIndex, "created_at_submit", submitCount ?? 0);
    setVariantField(index, vIndex, "variant_id", value);
    setVariantField(index, vIndex, "variant_label", opt?.label || "");
    setVariantField(index, vIndex, "unit_id", opt?.unit_id ?? "");
    setVariantField(index, vIndex, "unit_label", opt?.unit_label ?? "");
    setVariantField(index, vIndex, "is_not_listed", !!opt?.is_not_listed);
    // Prefill the price with the listed unit price; blank for "Not Listed".
    setVariantField(
      index,
      vIndex,
      "unit_price",
      opt?.is_not_listed || opt?.price == null ? "" : opt.price
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      {/* Header: Choose Service or Product | Choose A Service */}
      <Box sx={headerRowStyle}>
        <Box sx={headerColStyle}>
          <Typography sx={fieldLabelStyle}>{t("edit.choose_type")}</Typography>
          <Select
            fullWidth
            size="small"
            value={line.kind}
            onChange={(e) => changeKind(index, e.target.value)}
            sx={selectStyle}
          >
            <MenuItem value={LINE_KINDS.SERVICE}>
              {t("edit.type_service")}
            </MenuItem>
            <MenuItem value={LINE_KINDS.PRODUCT}>
              {t("edit.type_product")}
            </MenuItem>
          </Select>
        </Box>

        <Box sx={headerColStyle}>
          <Typography sx={fieldLabelStyle}>
            {t("edit.choose_a_service")}
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={line.service_id === "" ? "" : String(line.service_id)}
            onChange={(e) => handleServiceChange(e.target.value)}
            sx={selectStyle}
            MenuProps={menuProps}
            renderValue={(selected) => {
              if (selected === "" || selected == null) {
                return (
                  <Typography
                    sx={{ color: "rgba(62, 63, 76, 0.4)", fontSize: "14px" }}
                  >
                    {t("edit.choose_a_service")}
                  </Typography>
                );
              }
              const svc = services.find(
                (s) => String(s.id) === String(selected)
              );
              return line.service_label || svc?.label || selected;
            }}
          >
            {servicesLoading && services.length === 0
              ? [
                  <MenuItem
                    key="__loading"
                    disabled
                    sx={{ justifyContent: "center", py: "12px" }}
                  >
                    <CircularProgress
                      size={20}
                      sx={{ color: "rgba(36, 139, 112, 1)" }}
                    />
                  </MenuItem>,
                ]
              : services.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.label}
                  </MenuItem>
                ))}
          </Select>
          {showErrors && errors?.service_id && (
            <Typography sx={fieldErrorStyle}>{errors.service_id}</Typography>
          )}
        </Box>
      </Box>

      {/* Compact variant rows: [remove] variant · amount · unit · price */}
      {(line.variants || []).map((variant, vIndex) => {
        // A variant row added after the last failed save stays quiet until the
        // supplier tries to save again.
        const variantShowErrors =
          (submitCount ?? 0) > (variant.created_at_submit || 0);
        const variantErr = variantShowErrors
          ? errors?.variants?.[vIndex]
          : null;
        const listedPrice =
          Number(variant.unit_price) * Number(variant.amount || 0);
        // A variant is "chosen" once it has an id. Until then the unit shows an
        // (empty) dropdown and the price is blank — like Figma's fresh row.
        const isChosen =
          variant.variant_id !== "" && variant.variant_id != null;
        return (
          <Box key={variant.uid}>
            <Box sx={variantRowStyle}>
              <Box sx={variantLeftGroupStyle}>
              <IconButton
                size="small"
                onClick={() => removeVariant(index, vIndex)}
                sx={variantTrashStyle}
                aria-label={t("edit.remove_variant")}
              >
                <img src="/icons/delete-icon.svg" alt="" width="18" />
              </IconButton>

              {/* Variant (name + inline price / Not Listed) — doesn't grow (so
                  the gap before the price is kept) but can shrink so the whole
                  row stays on one line, even with a price input. */}
              <Box
                sx={{
                  // Mobile: fill the first wrapped line next to the trash icon.
                  flex: { xs: "1 1 160px", sm: "0 1 240px" },
                  minWidth: "110px",
                  position: "relative",
                }}
              >
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={
                    variant.variant_id === "" ? "" : String(variant.variant_id)
                  }
                  onChange={(e) => handleVariantChange(vIndex, e.target.value)}
                  sx={selectStyle}
                  MenuProps={menuProps}
                  renderValue={(selected) => {
                    if (selected === "" || selected == null) {
                      return (
                        <Typography
                          sx={{
                            color: "rgba(62, 63, 76, 0.4)",
                            fontSize: "14px",
                          }}
                        >
                          {t("edit.choose_variant")}
                        </Typography>
                      );
                    }
                    const opt = options.find(
                      (o) => String(o.variant_id) === String(selected)
                    );
                    const name = variant.variant_label || opt?.label || selected;
                    const priceText = variant.is_not_listed
                      ? `(${t("edit.not_listed")})`
                      : variant.unit_price !== "" && variant.unit_price != null
                        ? `${currencySymbol}${formatPrice(
                            variant.unit_price
                          )}/${variant.unit_label || t("edit.item")}`
                        : "";
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                          width: "100%",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {name}
                        </Box>
                        {priceText && (
                          <Box component="span" sx={variantInlinePriceStyle}>
                            {priceText}
                          </Box>
                        )}
                      </Box>
                    );
                  }}
                >
                  {options.map((o) => (
                    <MenuItem key={o.variant_id} value={String(o.variant_id)}>
                      {o.label}
                      {o.is_not_listed || o.price == null
                        ? ` (${t("edit.not_listed")})`
                        : ` ${currencySymbol}${formatPrice(o.price)}/${
                            o.unit_label || t("edit.item")
                          }`}
                    </MenuItem>
                  ))}
                </Select>
                {variantErr?.variant_id && (
                  <Typography sx={floatErrorStyle}>
                    {variantErr.variant_id}
                  </Typography>
                )}
              </Box>

              {/* Amount (labeled "Amount: N" per Figma) */}
              <Box
                sx={{
                  // Mobile: share the second wrapped line with the unit field.
                  flex: { xs: "1 1 45%", sm: "0 0 120px" },
                  position: "relative",
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  value={variant.amount}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || /^\d*\.?\d*$/.test(v)) {
                      setVariantField(index, vIndex, "amount", v);
                    }
                  }}
                  inputProps={{ inputMode: "decimal" }}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={amountAdornmentStyle}>
                        {t("edit.amount")}:
                      </Typography>
                    ),
                  }}
                  sx={textFieldStyle}
                />
                {variantErr?.amount && (
                  <Typography sx={floatErrorStyle}>
                    {variantErr.amount}
                  </Typography>
                )}
              </Box>

              {/* Unit: a fixed grey chip once a LISTED variant is chosen (its
                  unit is set by the pricing); otherwise (unchosen or Not
                  Listed) an editable dropdown. */}
              {isChosen && !variant.is_not_listed ? (
                <Box component="span" sx={unitChipStyle}>
                  {variant.unit_label || t("edit.item")}
                </Box>
              ) : (
              <Box sx={{ flex: { xs: "1 1 45%", sm: "0 0 120px" } }}>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={variant.unit_id === "" ? "" : String(variant.unit_id)}
                  onChange={(e) => {
                    const u = unitOptions.find(
                      (o) => String(o.id) === String(e.target.value)
                    );
                    setVariantField(index, vIndex, "unit_id", e.target.value);
                    setVariantField(index, vIndex, "unit_label", u?.label || "");
                  }}
                  sx={selectStyle}
                  MenuProps={menuProps}
                  renderValue={(selected) => {
                    if (selected === "" || selected == null) {
                      return (
                        <Typography
                          sx={{
                            color: "rgba(62, 63, 76, 0.4)",
                            fontSize: "14px",
                          }}
                        >
                          {variant.unit_label || t("edit.unit")}
                        </Typography>
                      );
                    }
                    const u = unitOptions.find(
                      (o) => String(o.id) === String(selected)
                    );
                    return variant.unit_label || u?.label || selected;
                  }}
                >
                  {unitOptions.map((u) => (
                    <MenuItem key={u.id} value={String(u.id)}>
                      {u.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              )}

              </Box>

              {/* Price: computed for listed variants, "$" input when Not Listed.
                  Sits at the row's far end (gap before it) like Figma. dir=ltr
                  keeps the currency symbol on the same side as the displays. */}
              <Box
                dir="ltr"
                sx={{
                  // Mobile: price drops to its own full-width line under the row,
                  // right-aligned (RTL side). flex-end = right edge in this
                  // dir=ltr box, which keeps ₪ before the input.
                  flex: { xs: "1 1 100%", sm: "0 0 auto" },
                  display: { xs: "flex" },
                  justifyContent: { xs: "flex-end" },
                  position: "relative",
                }}
              >
                {variant.is_not_listed ? (
                  // Currency sign sits OUTSIDE the input box (Figma: "$ [ ]").
                  // The parent Box is dir=ltr so the ₪ stays before the box.
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Typography
                      sx={{ color: "rgba(47, 43, 61, 0.6)", fontSize: "14px" }}
                    >
                      {currencySymbol}
                    </Typography>
                    <TextField
                      size="small"
                      placeholder={t("edit.price")}
                      value={variant.unit_price}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                          setVariantField(index, vIndex, "unit_price", v);
                        }
                      }}
                      // Keep the input's own text RTL (right-aligned placeholder)
                      // even though the wrapper is dir=ltr to place the ₪ outside.
                      inputProps={{ style: { direction: "rtl", textAlign: "right" } }}
                      sx={{ width: "84px", ...textFieldStyle }}
                    />
                  </Box>
                ) : isChosen ? (
                  <Typography sx={variantPriceBoxStyle}>
                    {currencySymbol}
                    {formatPrice(Number.isFinite(listedPrice) ? listedPrice : 0)}
                  </Typography>
                ) : null}
                {/* Only when the price input is actually shown — an unchosen
                    variant has no price field, and its own error covers it. */}
                {variant.is_not_listed && variantErr?.unit_price && (
                  <Typography dir="rtl" sx={floatErrorStyle}>
                    {variantErr.unit_price}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}

      <Button
        variant="text"
        startIcon={<AddCircleOutlineIcon fontSize="small" />}
        onClick={() => addVariant(index)}
        sx={addLinkStyle}
        disableRipple
      >
        {t("edit.add_variant")}
      </Button>
    </Box>
  );
};

export default ServiceLineCard;
